import { useState } from 'react';
import { Archive, Pencil, Plus, Trash2 } from 'lucide-react';
import type { CoachThread } from '../../../api/types';

interface Props {
  threads: CoachThread[];
  activeThreadId: number | null;
  onSelect: (id: number) => void;
  onNew: () => void;
  onRename?: (id: number, title: string) => void;
  onArchive?: (id: number) => void;
  onDelete?: (id: number) => void;
}

function formatRelative(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

const ICON = 'grid h-7 w-7 place-items-center rounded-[8px] text-muted hover:bg-surface hover:text-ink';

export default function CoachThreadSidebar({ threads, activeThreadId, onSelect, onNew, onRename, onArchive, onDelete }: Props) {
  const [editing, setEditing] = useState<number | null>(null);
  const [title, setTitle] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
  return (
    <div className="flex w-44 shrink-0 flex-col border-r border-line bg-surface-2">
      <button type="button" onClick={onNew}
        className="m-2 inline-flex items-center justify-center gap-1 rounded-[10px] bg-navy px-2 py-1.5 text-xs font-semibold text-white dark:bg-mist-fixed dark:text-navy">
        <Plus size={14} aria-hidden /> New chat
      </button>
      <div className="flex-1 overflow-y-auto">
        {threads.length === 0 && <p className="px-3 py-2 text-xs text-muted">No chats yet.</p>}
        {threads.map((t) => (
          <div key={t.id} className={`group border-l-2 ${t.id === activeThreadId ? 'border-accent bg-surface' : 'border-transparent'}`}>
            {editing === t.id ? (
              <form className="p-2" onSubmit={(e) => { e.preventDefault(); onRename?.(t.id, title.trim()); setEditing(null); }}>
                <input autoFocus value={title} onChange={(e) => setTitle(e.target.value.slice(0, 120))} aria-label="Chat title"
                  onBlur={() => setEditing(null)}
                  className="w-full rounded-[8px] border border-line bg-surface px-2 py-1 text-xs text-ink" />
              </form>
            ) : confirmDelete === t.id ? (
              <div className="p-2 text-xs text-ink">
                Delete this chat?
                <div className="mt-1 flex gap-2">
                  <button type="button" className="font-semibold text-danger-ink" onClick={() => { onDelete?.(t.id); setConfirmDelete(null); }}>Delete</button>
                  <button type="button" className="text-muted" onClick={() => setConfirmDelete(null)}>Cancel</button>
                </div>
              </div>
            ) : (
              <div className="flex items-center">
                <button type="button" onClick={() => onSelect(t.id)} data-testid={`coach-thread-row-${t.id}`}
                  className={`min-w-0 flex-1 px-3 py-2 text-left text-xs ${t.id === activeThreadId ? 'font-semibold text-ink' : 'text-muted-2 hover:text-ink'}`}>
                  <div className="truncate">{t.title || 'Untitled'}</div>
                  <div className="text-[10px] text-muted">{formatRelative(t.updated_at)}</div>
                </button>
                <div className="hidden flex-none pr-1 group-focus-within:flex group-hover:flex">
                  {onRename && <button type="button" className={ICON} aria-label={`Rename ${t.title || 'chat'}`} onClick={() => { setTitle(t.title); setEditing(t.id); }}><Pencil size={13} aria-hidden /></button>}
                  {onArchive && <button type="button" className={ICON} aria-label={`Archive ${t.title || 'chat'}`} onClick={() => onArchive(t.id)}><Archive size={13} aria-hidden /></button>}
                  {onDelete && <button type="button" className={ICON} aria-label={`Delete ${t.title || 'chat'}`} onClick={() => setConfirmDelete(t.id)}><Trash2 size={13} aria-hidden /></button>}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
